import React from 'react';
import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';

/**
 * Filter Panel Component
 * Displays filter options with multi-select dropdowns
 */
const FilterPanel = ({
  filters,
  filterOptions,
  onFilterChange,
  onClearFilters,
  className = ''
}) => {
  const hasActiveFilters = Object.values(filters).some(arr => arr.length > 0);

  const handleMultiSelectChange = (filterType, value, checked) => {
    const currentValues = filters[filterType] || [];
    let newValues;
    
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(v => v !== value);
    }
    
    onFilterChange(filterType, newValues);
  };

  const removeFilter = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    onFilterChange(filterType, currentValues.filter(v => v !== value));
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Filters</CardTitle>
          {hasActiveFilters && (
            <Button
              onClick={onClearFilters}
              variant="ghost"
              size="sm"
            >
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Active Filters Badges */}
        {hasActiveFilters && (
          <div className="mb-4 flex flex-wrap gap-2">
            {filters.appKey?.map((value) => (
              <Badge
                key={`app-${value}`}
                variant="secondary"
                className="gap-1"
              >
                App: {value}
                <button
                  onClick={() => removeFilter('appKey', value)}
                  className="ml-1 hover:bg-secondary/80 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {filters.country?.map((value) => (
              <Badge
                key={`country-${value}`}
                variant="secondary"
                className="gap-1"
              >
                Country: {value}
                <button
                  onClick={() => removeFilter('country', value)}
                  className="ml-1 hover:bg-secondary/80 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {filters.adUnit?.map((value) => (
              <Badge
                key={`adunit-${value}`}
                variant="secondary"
                className="gap-1"
              >
                Ad Unit: {value}
                <button
                  onClick={() => removeFilter('adUnit', value)}
                  className="ml-1 hover:bg-secondary/80 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {filters.adSource?.map((value) => (
              <Badge
                key={`adsource-${value}`}
                variant="secondary"
                className="gap-1"
              >
                Ad Source: {value}
                <button
                  onClick={() => removeFilter('adSource', value)}
                  className="ml-1 hover:bg-secondary/80 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Filter Dropdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* App Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              App
            </label>
            <div className="border rounded-md max-h-48 overflow-y-auto">
              {filterOptions.apps && filterOptions.apps.length > 0 ? (
                filterOptions.apps.map((app) => (
                  <label
                    key={app.app_key || app.breakdown_value}
                    className="flex items-center px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={(filters.appKey || []).includes(app.app_key || app.breakdown_value)}
                      onCheckedChange={(checked) =>
                        handleMultiSelectChange(
                          'appKey',
                          app.app_key || app.breakdown_value,
                          checked
                        )
                      }
                      className="mr-2"
                    />
                    <span className="text-sm">
                      {app.app_name || app.app_key || app.breakdown_value}
                    </span>
                  </label>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">No apps available</div>
              )}
            </div>
          </div>

          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Country
            </label>
            <div className="border rounded-md max-h-48 overflow-y-auto">
              {filterOptions.countries && filterOptions.countries.length > 0 ? (
                filterOptions.countries.map((country) => (
                  <label
                    key={country.country || country.breakdown_value}
                    className="flex items-center px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={(filters.country || []).includes(country.country || country.breakdown_value)}
                      onCheckedChange={(checked) =>
                        handleMultiSelectChange(
                          'country',
                          country.country || country.breakdown_value,
                          checked
                        )
                      }
                      className="mr-2"
                    />
                    <span className="text-sm">
                      {country.country_name || country.country || country.breakdown_value}
                    </span>
                  </label>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">No countries available</div>
              )}
            </div>
          </div>

          {/* Ad Unit Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Ad Unit
            </label>
            <div className="border rounded-md max-h-48 overflow-y-auto">
              {filterOptions.adUnits && filterOptions.adUnits.length > 0 ? (
                filterOptions.adUnits.map((adUnit) => (
                  <label
                    key={adUnit.ad_unit || adUnit.breakdown_value}
                    className="flex items-center px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={(filters.adUnit || []).includes(adUnit.ad_unit || adUnit.breakdown_value)}
                      onCheckedChange={(checked) =>
                        handleMultiSelectChange(
                          'adUnit',
                          adUnit.ad_unit || adUnit.breakdown_value,
                          checked
                        )
                      }
                      className="mr-2"
                    />
                    <span className="text-sm">
                      {adUnit.ad_unit_name || adUnit.ad_unit || adUnit.breakdown_value}
                    </span>
                  </label>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">No ad units available</div>
              )}
            </div>
          </div>

          {/* Ad Source Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Ad Source
            </label>
            <div className="border rounded-md max-h-48 overflow-y-auto">
              {filterOptions.adSources && filterOptions.adSources.length > 0 ? (
                filterOptions.adSources.map((adSource) => (
                  <label
                    key={adSource.ad_source || adSource.breakdown_value}
                    className="flex items-center px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <Checkbox
                      checked={(filters.adSource || []).includes(adSource.ad_source || adSource.breakdown_value)}
                      onCheckedChange={(checked) =>
                        handleMultiSelectChange(
                          'adSource',
                          adSource.ad_source || adSource.breakdown_value,
                          checked
                        )
                      }
                      className="mr-2"
                    />
                    <span className="text-sm">
                      {adSource.ad_source || adSource.breakdown_value}
                    </span>
                  </label>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">No ad sources available</div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterPanel;
